import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import {
  useGetAgencyCompliance,
  getAgencyReconciliation,
} from "../../../../lib/api-client-react/src/agency/agency";
import {
  type AgencyCompliance,
  type AgencyRelease,
  shopAgencyStatus,
} from "../../../../lib/agency-contracts";
import { AgencyReconciliationView } from "@/components/AgencyReconciliationView";

const date = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString("en-GB") : "Not set";
const Badge = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-block rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
    {children}
  </span>
);
export default function AdminAgencyCompliance() {
  const query = useGetAgencyCompliance<AgencyCompliance>({
    query: { queryKey: ["agency-compliance"], retry: false },
  });
  const [search, setSearch] = useState(""),
    [activeOnly, setActiveOnly] = useState(true),
    [preview, setPreview] = useState<AgencyRelease | null>(null),
    [orderId, setOrderId] = useState(""),
    [report, setReport] = useState<Record<string, unknown> | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function checkOrder() {
    if (!/^[1-9][0-9]*$/.test(orderId)) {
      setError("Enter an order number.");
      return;
    }
    setBusy(true);
    setError("");
    setReport(null);
    try {
      const r = await getAgencyReconciliation(Number(orderId));
      if (typeof r === "object") setReport(r as Record<string, unknown>);
    } catch {
      setError("Could not load this order. Please retry.");
    } finally {
      setBusy(false);
    }
  }
  const data = query.data,
    stores =
      data?.stores.filter(
        (s) =>
          (!activeOnly || (s.is_active && s.approved)) &&
          s.name.toLowerCase().includes(search.toLowerCase()),
      ) ?? [];
  return (
    <AdminLayout>
      <div className="mx-auto max-w-6xl space-y-6">
        <header>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Agreements & evidence
          </p>
          <h1 className="mt-1 text-3xl font-bold text-slate-900">
            Marketplace compliance
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Contracts, pricing mandates and reconciliation evidence. Drafts do
            not change live agreements, prices or payouts.
          </p>
        </header>
        {query.isPending ? (
          <p role="status">Loading compliance records…</p>
        ) : query.isError ? (
          <div role="alert" className="rounded-xl bg-rose-50 p-5">
            Compliance records could not be loaded.{" "}
            <button className="underline" onClick={() => void query.refetch()}>
              Retry
            </button>
          </div>
        ) : (
          data && (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                {[
                  [
                    "Active shops",
                    String(
                      data.stores.filter((s) => s.is_active && s.approved)
                        .length,
                    ),
                  ],
                  [
                    "New agency acceptances",
                    String(
                      data.stores.filter((s) => s.agency_acceptance).length,
                    ),
                  ],
                  [
                    "VAT registration",
                    data.vatRegistration.verified ? "Verified" : "Unresolved",
                  ],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-2xl border bg-white p-5">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="mt-1 text-2xl font-bold">{value}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                <h2 className="font-semibold">Rollout remains controlled</h2>
                <p className="mt-1 text-sm">{data.rollout}</p>
                <p className="mt-2 text-sm">{data.deliveryStatus}</p>
                <details className="mt-3 text-sm">
                  <summary className="cursor-pointer font-semibold">
                    Outstanding driver and delivery reviews
                  </summary>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {data.driverReviewItems.map((i) => (
                      <li key={i}>{i}</li>
                    ))}
                  </ul>
                </details>
              </div>
              <section>
                <h2 className="text-xl font-bold">Agreement versions</h2>
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  {data.releases.map((r) => (
                    <article
                      key={r.id}
                      className="min-w-0 rounded-2xl border bg-white p-5"
                    >
                      <Badge>{r.state.status}</Badge>
                      <p className="mt-3 text-xs uppercase text-slate-500">
                        {r.audience}
                      </p>
                      <h3 className="break-words font-semibold">{r.version}</h3>
                      <p className="mt-2 text-sm text-slate-600">
                        Effective: {date(r.state.effectiveAt)}
                      </p>
                      <p className="mt-2 text-xs text-slate-500">
                        Missing written reviews:{" "}
                        {r.state.missing.join(", ") ||
                          "None recorded as missing"}
                      </p>
                      <button
                        onClick={() => setPreview(r)}
                        className="mt-4 rounded-lg border px-4 py-2 text-sm font-semibold"
                      >
                        Read exact version
                      </button>
                    </article>
                  ))}
                </div>
              </section>
              <section className="rounded-2xl border bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-xl font-bold">Shop agreements</h2>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={activeOnly}
                      onChange={(e) => setActiveOnly(e.target.checked)}
                    />
                    Active shops only
                  </label>
                </div>
                <input
                  aria-label="Search shops"
                  placeholder="Search shops"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="mt-4 w-full rounded-xl border p-3"
                />
                <div className="mt-4 space-y-3">
                  {!stores.length && (
                    <p className="text-sm text-slate-500">
                      No shops match this search.
                    </p>
                  )}
                  {stores.map((s) => (
                    <article key={s.id} className="rounded-xl bg-slate-50 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold">{s.name}</h3>
                          <p className="text-xs text-slate-500">
                            Shop #{s.id} · Current configured uplift{" "}
                            {s.menu_uplift_percent ?? 0}%
                          </p>
                        </div>
                        <Badge>{shopAgencyStatus(s)}</Badge>
                      </div>
                      <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                        <p>
                          Legacy version:{" "}
                          {s.legacy_acceptance?.version ??
                            "No recorded signature"}
                          <br />
                          <span className="text-xs text-slate-500">
                            Current legacy version:{" "}
                            {s.legacy_current_version ?? "None"}
                          </span>
                        </p>
                        <p>
                          Agency version:{" "}
                          {s.agency_acceptance?.version ?? "Not accepted"}
                          <br />
                          <span className="text-xs text-slate-500">
                            Effective: {date(s.agency_acceptance?.effectiveAt)}{" "}
                            · Pricing mandate:{" "}
                            {s.agency_acceptance
                              ? "Recorded with signature"
                              : "Pending approval and acceptance"}
                          </span>
                        </p>
                      </div>
                      <p className="mt-2 text-xs text-slate-500">
                        VAT details: unverified. No VAT invoice is authorised by
                        this status.
                      </p>
                    </article>
                  ))}
                </div>
              </section>
              <section className="rounded-2xl border bg-white p-5">
                <h2 className="text-xl font-bold">
                  Order reconciliation companion
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Read-only payment and allocation evidence. Legacy Z values
                  stay unchanged. Card verification may take a few seconds.
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void checkOrder();
                  }}
                  className="mt-4 flex flex-wrap gap-2"
                >
                  <input
                    aria-label="Order number"
                    inputMode="numeric"
                    placeholder="Order number"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    className="min-w-0 flex-1 rounded-xl border p-3"
                  />
                  <button
                    disabled={busy}
                    className="rounded-xl bg-slate-900 px-5 py-3 font-semibold text-white disabled:opacity-50"
                  >
                    {busy ? "Checking…" : "Check order"}
                  </button>
                </form>
                {error && (
                  <p role="alert" className="mt-3 text-sm text-rose-700">
                    {error}
                  </p>
                )}
                {report && <AgencyReconciliationView report={report} />}
              </section>
              <section className="rounded-2xl border bg-white p-5">
                <h2 className="text-xl font-bold">
                  Prospective uplift history
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Most recent 100 changes since this infrastructure was
                  installed. Missing historical rates are not reconstructed.
                </p>
                {!data.upliftHistory.length ? (
                  <p className="mt-4 text-sm">
                    No prospective changes recorded yet.
                  </p>
                ) : (
                  <div className="mt-4 space-y-3">
                    {data.upliftHistory.map((h) => (
                      <div
                        key={h.id}
                        className="break-words rounded-xl bg-slate-50 p-4 text-sm"
                      >
                        <p className="font-semibold">
                          Shop #{h.store_id} · {h.scope} #{h.scope_id}
                        </p>
                        <p>
                          {date(h.effective_at)} · Administrator{" "}
                          {h.administrator_id ?? "not supplied"}
                        </p>
                        <p className="mt-1 break-all text-xs">
                          {JSON.stringify(h.previous_value)} →{" "}
                          {JSON.stringify(h.new_value)}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {h.reason ?? h.source}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )
        )}
        {preview && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Agreement version"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3"
            onClick={() => setPreview(null)}
          >
            <div
              className="flex max-h-[92dvh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-3 border-b p-4">
                <h2 className="min-w-0 break-words font-bold">
                  {preview.version}
                </h2>
                <button
                  className="shrink-0 rounded-lg border px-3 py-2"
                  onClick={() => setPreview(null)}
                >
                  Close
                </button>
              </div>
              <div className="overflow-y-auto p-5">
                <p className="mb-4 break-all text-xs text-slate-500">
                  SHA-256: {preview.content_hash}
                </p>
                <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-7">
                  {preview.content}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
